import { useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate, Link } from "react-router-dom";
import { Home, FileText, QrCode, CreditCard, Users, Settings, Inbox, Shield, LogOut, Menu, X, Eye, Plus, ChevronLeft, Rss } from "lucide-react";
import { useAuth, isAdmin } from "../lib/auth";
import { api } from "../lib/api";

export const Logo = ({ className = "" }) => (
  <div className={`flex items-center font-extrabold tracking-tight text-[28px] leading-none text-[#15fabc] ${className}`} data-testid="tapm-logo">
    tapm<Rss className="h-5 w-5 rotate-45 -ml-0.5 mt-1" strokeWidth={3} />
  </div>
);

export function ImpersonationBanner() {
  const { me } = useAuth();
  if (!me?.impersonated_by) return null;
  const exit = async () => { await api.post("/auth/impersonation/exit"); window.location.href = "/superadmin/accounts"; };
  return (
    <div className="sticky top-0 z-50 bg-amber-400 text-black text-sm font-medium px-4 py-2 flex items-center justify-center gap-3" data-testid="impersonation-banner">
      <Eye className="h-4 w-4" /> You are viewing TapM as {me.user.email} (impersonated by {me.impersonated_by}).
      <button onClick={exit} className="underline font-semibold" data-testid="exit-impersonation-btn">Exit impersonation</button>
    </div>
  );
}

const NAV = [
  ["/dashboard", "Home", Home, true],
  ["/dashboard/pages", "Pages", FileText],
  ["/dashboard/qr", "QR codes", QrCode],
  ["/dashboard/cards", "Cards", CreditCard],
  ["/dashboard/leads", "Leads", Inbox],
  ["/dashboard/team", "Team", Users, false, true],
];

export const navCls = ({ isActive }) => `flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#15fabc] text-[#220b48]" : "text-[#3D4136] hover:bg-[#EEF0E9]"}`;

function SideNav({ onNav }) {
  const { me } = useAuth();
  const nav = useNavigate();
  const logout = async () => { await api.post("/auth/logout"); nav("/login"); window.location.reload(); };
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-2 mb-8"><Logo /><ChevronLeft className="h-4 w-4 text-[#8C9085] hidden lg:block" /></div>
      <nav className="space-y-1 flex-1">
        {NAV.filter((n) => !n[4] || isAdmin(me)).map(([to, label, Icon, end]) => (
          <NavLink key={to} to={to} end={end} onClick={onNav} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`} className={navCls}>
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="space-y-1">
        {me?.user?.is_superadmin && !me.impersonated_by && (
          <NavLink to="/superadmin" onClick={onNav} data-testid="nav-superadmin" className={navCls}><Shield className="h-4 w-4" /> Superadmin</NavLink>
        )}
        <NavLink to="/dashboard/settings" onClick={onNav} data-testid="nav-settings" className={navCls}><Settings className="h-4 w-4" /> Settings</NavLink>
        <button onClick={logout} className="w-full flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium text-[#3D4136] hover:bg-[#EEF0E9]" data-testid="logout-btn"><LogOut className="h-4 w-4" /> Log out</button>
      </div>
    </div>
  );
}

function TopBar({ onMenu }) {
  const { me } = useAuth();
  const initials = `${me?.user?.first_name?.[0] || ""}${me?.user?.last_name?.[0] || ""}` || "?";
  return (
    <header className="h-16 flex items-center justify-between gap-3 px-5 md:px-8 sticky top-0 z-30 bg-[#F3F4EF]/90 backdrop-blur-md">
      <button className="lg:hidden" onClick={onMenu} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
      <div className="flex-1" />
      <Link to="/dashboard/pages" className="h-9 w-9 grid place-items-center rounded-full bg-white border border-[#E4E6DE] hover:bg-[#EEF0E9]" title="New page" data-testid="topbar-new-btn"><Plus className="h-4 w-4" /></Link>
      <Link to="/dashboard/settings/account" className="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full hover:bg-white" data-testid="topbar-user">
        <span className="h-8 w-8 rounded-full bg-[#220b48] text-[#15fabc] grid place-items-center text-xs font-bold">{initials}</span>
        <span className="hidden sm:block leading-tight"><span className="block text-xs text-[#8C9085]" data-testid="sidebar-user-name">{me?.user?.first_name} {me?.user?.last_name}</span><span className="block text-sm font-semibold">{me?.org?.name}</span></span>
      </Link>
    </header>
  );
}

export function RequireAuth({ children }) {
  const { me } = useAuth();
  if (me === null) return <div className="app-light grid place-items-center text-[#8C9085]">Loading…</div>;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname)}`} replace />;
  if (me.needs_onboarding) return <Navigate to="/onboarding" replace />;
  return children;
}

export default function AppShell() {
  const [open, setOpen] = useState(false);
  return (
    <RequireAuth>
      <div className="app-light">
        <ImpersonationBanner />
        <aside className="hidden lg:block w-64 p-3 h-screen fixed left-0 top-0 z-40"><div className="h-full bg-white rounded-3xl border border-[#E9EBE3] p-4"><SideNav /></div></aside>
        {open && (
          <div className="fixed inset-0 z-50 bg-black/30 lg:hidden" onClick={() => setOpen(false)}>
            <div className="w-72 h-full bg-white p-4" onClick={(e) => e.stopPropagation()}>
              <button className="absolute top-5 left-60" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
              <SideNav onNav={() => setOpen(false)} />
            </div>
          </div>
        )}
        <div className="lg:ml-64">
          <TopBar onMenu={() => setOpen(true)} />
          <main className="px-5 md:px-8 pb-10 pt-2 max-w-[1400px]"><Outlet /></main>
        </div>
      </div>
    </RequireAuth>
  );
}
